import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, of, catchError, map, throwError } from 'rxjs';
import { API_BASE_URL, API_ROUTES } from '@core/constants/api.constants';
import { State, StateService } from 'src/app/core/services/state.service';
import { City, CityService } from 'src/app/core/services/city.service';

export interface Role {
  id: number;
  name: string;
  description: string;
}

export interface User {
  id: number;
  email: string;
  name: string;
  last_name: string;
  phone_number?: string;
  is_active: boolean;
  document_type?: string;
  document_number?: string;
  gender?: string;
  date_birth?: string;
  address?: string;
  how_to_know?: string;
  role: Role;
  state?: State;
  city?: City;
  created_at?: string;
  updated_at?: string;
}

export interface Child {
  id: number;
  first_name: string;
  last_name: string;
  birth_date: string;
  created_at: string;
  updated_at: string;
}

export interface UserWithChildren extends User {
  children: Child[];
}

export interface CreateUserRequest {
  email: string;
  password: string;
  name: string;
  last_name: string;
  phone_number?: string;
  role?: number; // ID del rol (1=admin, 2=analyst, 3=customer)
  document_type?: string;
  document_number?: string;
  gender?: string;
  date_birth?: string;
  address?: string;
  how_to_know?: string;
  state?: number; // ID del estado
  city?: number; // ID de la ciudad
}

export interface UpdateUserRequest {
  email?: string;
  name?: string;
  last_name?: string;
  phone_number?: string;
  role?: number; // ID del rol (1=admin, 2=analyst, 3=customer)
  document_type?: string;
  document_number?: string;
  gender?: string;
  date_birth?: string;
  address?: string;
  how_to_know?: string;
  state?: number; // ID del estado
  city?: number; // ID de la ciudad
  is_active?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class UsersService {
  private readonly baseUrl = API_BASE_URL;

  constructor(
    private http: HttpClient,
    private stateService: StateService,
    private cityService: CityService
  ) { }

  /**
   * Obtener headers con autenticación
   */
  private getAuthHeaders(): HttpHeaders {
    const token = localStorage.getItem('access_token');
    return new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    });
  }

  /**
   * Manejo de errores
   */
  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      console.error(`${operation} failed:`, error);
      
      // En caso de error, devolver datos de ejemplo para que la UI funcione
      if (operation === 'getUsers') {
        const mockUsers: User[] = [
          {
            id: 1,
            email: 'maria@example.com',
            name: 'María',
            last_name: 'Rodríguez',
            phone_number: '+57 300 123 4567',
            is_active: true,
            role: { id: 3, name: 'customer', description: 'Cliente o usuario normal de la plataforma' },
            created_at: '2024-01-15T10:30:00Z',
            updated_at: '2024-01-15T10:30:00Z'
          },
          {
            id: 2,
            email: 'juan@example.com',
            name: 'Juan',
            last_name: 'Pérez',
            phone_number: '+57 301 234 5678',
            is_active: true,
            role: { id: 3, name: 'customer', description: 'Cliente o usuario normal de la plataforma' },
            created_at: '2024-01-16T11:30:00Z',
            updated_at: '2024-01-16T11:30:00Z'
          },
          {
            id: 3,
            email: 'admin@example.com',
            name: 'Administrador',
            last_name: 'Sistema',
            phone_number: '+57 302 345 6789',
            is_active: true,
            role: { id: 1, name: 'admin', description: 'Administrador del sistema con acceso total' },
            created_at: '2024-01-10T09:00:00Z',
            updated_at: '2024-01-10T09:00:00Z'
          }
        ];
        return of(mockUsers as any);
      }
      
      return of(result as T);
    };
  }

  /**
   * GET /api/v1/users - Listar todos los usuarios
   */
  getUsers(): Observable<User[]> {
    const url = `${this.baseUrl}${API_ROUTES.USERS.GET_ALL}`;
    return this.http.get<User[]>(url, { headers: this.getAuthHeaders() })
      .pipe(
        catchError(this.handleError<User[]>('getUsers', []))
      );
  }

  /**
   * GET /api/v1/users/{user_id} - Obtener usuario por ID
   */
  getUserById(userId: number): Observable<User> {
    const url = `${this.baseUrl}${API_ROUTES.USERS.GET_BY_ID(userId)}`;
    return this.http.get<User>(url, { headers: this.getAuthHeaders() })
      .pipe(
        catchError(this.handleError<User>('getUserById'))
      );
  }

  /**
   * GET /api/v1/users/{user_id}/children - Obtener usuario con hijos
   */
  getUserWithChildren(userId: number): Observable<UserWithChildren> {
    const url = `${this.baseUrl}${API_ROUTES.USERS.GET_WITH_CHILDREN(userId)}`;
    return this.http.get<UserWithChildren>(url, { headers: this.getAuthHeaders() })
      .pipe(
        catchError(this.handleError<UserWithChildren>('getUserWithChildren'))
      );
  }

  /**
   * POST /api/v1/users - Crear nuevo usuario
   */
  createUser(userData: CreateUserRequest): Observable<User> {
    const url = `${this.baseUrl}${API_ROUTES.USERS.CREATE}`;
    return this.http.post<User>(url, userData, { headers: this.getAuthHeaders() })
      .pipe(
        catchError(this.handleError<User>('createUser'))
      );
  }

  /**
   * Cambiar estado activo/inactivo del usuario
   */
  toggleUserStatus(userId: number): Observable<User> {
    return this.http.patch<User>(`${this.baseUrl}/users/${userId}/toggle-status`, {}, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(error => {
        console.error('Error al cambiar estado del usuario:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * PUT /api/v1/users/{user_id} - Actualizar usuario
   */
  updateUser(userId: number, userData: UpdateUserRequest): Observable<User> {
    const url = `${this.baseUrl}${API_ROUTES.USERS.UPDATE(userId)}`;
    return this.http.put<User>(url, userData, { headers: this.getAuthHeaders() })
      .pipe(
        catchError(this.handleError<User>('updateUser'))
      );
  }

  /**
   * DELETE /api/v1/users/{user_id} - Eliminar usuario
   */
  deleteUser(userId: number): Observable<{ message: string }> {
    const url = `${this.baseUrl}${API_ROUTES.USERS.DELETE(userId)}`;
    return this.http.delete<{ message: string }>(url, { headers: this.getAuthHeaders() })
      .pipe(
        catchError(this.handleError<{ message: string }>('deleteUser', { message: 'Error al eliminar usuario' }))
      );
  }

  /**
   * Buscar usuarios por término
   */
  searchUsers(term: string): Observable<User[]> {
    if (!term.trim()) {
      return of([]);
    }
    
    // Primero obtenemos todos los usuarios y luego filtramos localmente
    return this.getUsers().pipe(
      map(users => users.filter(user => 
        user.name.toLowerCase().includes(term.toLowerCase()) ||
        user.last_name.toLowerCase().includes(term.toLowerCase()) ||
        user.email.toLowerCase().includes(term.toLowerCase()) ||
        user.role.name.toLowerCase().includes(term.toLowerCase()) ||
        user.id.toString().includes(term)
      ))
    );
  }

  /**
   * GET /api/v1/states - Obtener todos los estados
   */
  getStates(): Observable<State[]> {
    // Usamos el nuevo servicio de estados con datos estáticos locales
    return this.stateService.getStates();
  }

  /**
   * GET /api/v1/cities/state/{stateId} - Obtener ciudades por estado
   */
  getCitiesByState(stateId: number): Observable<City[]> {
    // Usamos el nuevo servicio de ciudades con datos estáticos locales
    return this.cityService.getCitiesByState(stateId);
  }

  /**
   * Obtener solo usuarios con rol cliente y estado activo
   * Útil para seleccionar un usuario al crear un estudiante
   */
  getActiveClientUsers(): Observable<User[]> {
    return this.getUsers().pipe(
      map(users => users.filter(user => 
        user.is_active === true && 
        user.role && 
        user.role.name === 'customer'
      )),
      catchError(this.handleError<User[]>('getActiveClientUsers', []))
    );
  }
}
